import type { Document } from '@agentwiki/shared'
import { buildDocumentResultPresentation } from './document-result-presentation'
import {
  formatSourceRevision,
  getDocumentSourceIdentity,
  sourceIdentityMatches,
} from './document-source-identity'
import { LIBRARY_FIXED_VIEW_FILTERS, type LibraryQueryState } from './knowledge-library-query-state'
import { deriveEffectiveAuthorityState } from './source-authority-metadata'

export interface SourceFacet {
  key: string
  label: string
  kindLabel: string
  count: number
}

export interface MemoryClassFacet {
  key: string
  count: number
}

function normalized(value: string | null | undefined) {
  return (value ?? '').trim().toLocaleLowerCase()
}

function documentAuthority(doc: Document) {
  const source = doc.sourceProvenance
  const value = source?.authorityState
    ? deriveEffectiveAuthorityState(source.authorityState, source.authorityExpiresAt)
    : null
  return value === 'active' ||
    value === 'reference' ||
    value === 'needs-review' ||
    value === 'superseded' ||
    value === 'expired'
    ? value
    : null
}

function matchesQuery(doc: Document, query: string) {
  const needle = normalized(query)
  if (!needle) return true
  const source = doc.sourceProvenance
  const identity = source ? getDocumentSourceIdentity(source) : null
  return [
    doc.title,
    doc.summary,
    doc.description,
    doc.category,
    source?.sourcePath,
    source?.memoryClass,
    identity?.label,
    identity?.kindLabel,
  ]
    .map(normalized)
    .join(' ')
    .includes(needle)
}

function matchesFixedView(doc: Document, state: LibraryQueryState) {
  const filter = LIBRARY_FIXED_VIEW_FILTERS[state.view]
  if ('memoryClass' in filter && doc.sourceProvenance?.memoryClass !== filter.memoryClass) {
    return false
  }
  if ('sourceType' in filter && doc.sourceProvenance?.sourceType !== filter.sourceType) return false
  if ('authority' in filter && documentAuthority(doc) !== filter.authority) return false
  return true
}

function matchesFacets(doc: Document, state: LibraryQueryState, authorityAvailable: boolean) {
  const presentation = buildDocumentResultPresentation({
    ...doc,
    effectiveAuthorityState: documentAuthority(doc),
  })
  if (state.ownership !== 'all' && presentation.ownership.key !== state.ownership) return false
  if (state.access !== 'all' && doc.accessLevel !== state.access) return false
  if (state.source !== 'all' && !sourceIdentityMatches(doc.sourceProvenance, state.source)) {
    return false
  }
  if (state.sync !== 'all' && presentation.syncHealth.key !== state.sync) return false
  if (
    authorityAvailable &&
    state.authority !== 'all' &&
    documentAuthority(doc) !== state.authority
  ) {
    return false
  }
  if (state.memoryClass !== 'all' && doc.sourceProvenance?.memoryClass !== state.memoryClass) {
    return false
  }
  return matchesQuery(doc, state.query)
}

function dateValue(value: Date | string | number) {
  const timestamp = value instanceof Date ? value.getTime() : new Date(value).getTime()
  return Number.isFinite(timestamp) ? timestamp : 0
}

function sortDocuments(documents: Document[], state: LibraryQueryState) {
  const direction = state.order === 'asc' ? 1 : -1
  return [...documents].sort((left, right) => {
    const comparison =
      state.sort === 'title'
        ? left.title.localeCompare(right.title)
        : state.sort === 'created'
          ? dateValue(left.createdAt) - dateValue(right.createdAt)
          : dateValue(left.updatedAt) - dateValue(right.updatedAt)
    return comparison === 0 ? direction * left.id.localeCompare(right.id) : direction * comparison
  })
}

export function buildSourceFacets(documents: Document[]): SourceFacet[] {
  const facets = new Map<string, SourceFacet>()
  for (const doc of documents) {
    if (!doc.sourceProvenance) continue
    const identity = getDocumentSourceIdentity(doc.sourceProvenance)
    const current = facets.get(identity.key)
    facets.set(identity.key, { ...identity, count: (current?.count ?? 0) + 1 })
  }
  return [...facets.values()].sort(
    (left, right) => right.count - left.count || left.label.localeCompare(right.label),
  )
}

export function buildMemoryClassFacets(documents: Document[]): MemoryClassFacet[] {
  const counts = new Map<string, number>()
  for (const doc of documents) {
    const memoryClass = doc.sourceProvenance?.memoryClass
    if (memoryClass) counts.set(memoryClass, (counts.get(memoryClass) ?? 0) + 1)
  }
  return [...counts.entries()]
    .map(([key, count]) => ({ key, count }))
    .sort((left, right) => right.count - left.count || left.key.localeCompare(right.key))
}

export function buildKnowledgeLibraryState(documents: Document[], state: LibraryQueryState) {
  const presentations = documents.map((doc) => buildDocumentResultPresentation(doc))
  const sourceBacked = presentations.filter((item) => item.ownership.key === 'source-backed')
  const syncWarnings = sourceBacked.filter(
    (item) => item.syncHealth.key === 'stale' || item.syncHealth.key === 'conflict',
  )
  const authorityAvailable = documents.some((doc) => documentAuthority(doc) !== null)
  const filteredDocs = sortDocuments(
    documents.filter(
      (doc) => matchesFixedView(doc, state) && matchesFacets(doc, state, authorityAvailable),
    ),
    state,
  )

  return {
    filteredDocs,
    sourceFacets: buildSourceFacets(documents),
    authorityAvailable,
    stats: {
      loaded: documents.length,
      matching: filteredDocs.length,
      sourceBacked: sourceBacked.length,
      synced: sourceBacked.length - syncWarnings.length,
      syncWarnings: syncWarnings.length,
      manual: documents.length - sourceBacked.length,
    },
  }
}

function sourceRevision(doc: Document) {
  return doc.sourceProvenance ? formatSourceRevision(doc.sourceProvenance) : null
}

export function buildActiveSourceOverview(documents: Document[], sourceKey: string) {
  const sourceDocs = documents.filter(
    (doc) =>
      doc.sourceProvenance &&
      (sourceKey === 'all' || sourceIdentityMatches(doc.sourceProvenance, sourceKey)),
  )
  const identities = buildSourceFacets(sourceDocs)
  const revisions = new Set(
    sourceDocs.map(sourceRevision).filter((value): value is string => Boolean(value)),
  )
  const latestSyncedAt = sourceDocs.reduce(
    (latest, doc) => Math.max(latest, doc.sourceProvenance?.lastSyncedAt ?? 0),
    0,
  )

  return {
    label: sourceKey === 'all' ? 'All loaded sources' : (identities[0]?.label ?? 'Unknown source'),
    kindLabel: sourceKey === 'all' ? 'Source overview' : (identities[0]?.kindLabel ?? 'Source'),
    count: sourceDocs.length,
    revisionLabel:
      identities.length > 1 || revisions.size > 1
        ? 'Mixed revisions'
        : ([...revisions][0] ?? 'No revision metadata'),
    latestSyncedAt: latestSyncedAt || null,
  }
}
